const API = import.meta.env.VITE_API_URL || ''

// Gerätesprache → DeepL-Zielsprachcode. DeepL verlangt bei Englisch,
// Portugiesisch und Chinesisch eine Variante.
function deviceTargetLang() {
  const locale = (navigator.languages?.[0] || navigator.language || 'en').toLowerCase()
  const [lang, region = ''] = locale.split('-')
  if (lang === 'en') return region === 'gb' ? 'EN-GB' : 'EN-US'
  if (lang === 'pt') return region === 'pt' ? 'PT-PT' : 'PT-BR'
  if (lang === 'zh') return ['tw', 'hk', 'mo', 'hant'].includes(region) ? 'ZH-HANT' : 'ZH-HANS'
  return lang.toUpperCase()
}

export const TARGET_LANG = deviceTargetLang()

const DEVICE_LOCALE = navigator.languages?.[0] || navigator.language || 'en'
const DEVICE_LANG = DEVICE_LOCALE.toLowerCase().split('-')[0]

// UI-Texte rund um die Übersetzung in der Gerätesprache (wie bei Facebook/Instagram).
// {lang} wird durch den Sprachnamen in der Gerätesprache ersetzt.
const UI_STRINGS = {
  en: { see: 'See translation', original: 'See original', translating: 'Translating…', from: 'Translated from {lang}', same: 'Already in your language' },
  de: { see: 'Übersetzung anzeigen', original: 'Original anzeigen', translating: 'Wird übersetzt…', from: 'Übersetzt aus {lang}', same: 'Bereits in deiner Sprache' },
  fr: { see: 'Voir la traduction', original: "Voir l'original", translating: 'Traduction…', from: 'Traduit de : {lang}', same: 'Déjà dans votre langue' },
  es: { see: 'Ver traducción', original: 'Ver original', translating: 'Traduciendo…', from: 'Traducido del {lang}', same: 'Ya está en tu idioma' },
  it: { see: 'Visualizza traduzione', original: 'Visualizza originale', translating: 'Traduzione in corso…', from: 'Tradotto da: {lang}', same: 'Già nella tua lingua' },
  pt: { see: 'Ver tradução', original: 'Ver original', translating: 'Traduzindo…', from: 'Traduzido do {lang}', same: 'Já está no seu idioma' },
  nl: { see: 'Vertaling bekijken', original: 'Origineel bekijken', translating: 'Vertalen…', from: 'Vertaald uit het {lang}', same: 'Al in je taal' },
  pl: { see: 'Zobacz tłumaczenie', original: 'Zobacz oryginał', translating: 'Tłumaczenie…', from: 'Przetłumaczono z: {lang}', same: 'Już w Twoim języku' },
  tr: { see: 'Çeviriyi gör', original: 'Orijinali gör', translating: 'Çevriliyor…', from: '{lang} dilinden çevrildi', same: 'Zaten kendi dilinizde' },
  ru: { see: 'Показать перевод', original: 'Показать оригинал', translating: 'Перевод…', from: 'Переведено с языка: {lang}', same: 'Уже на вашем языке' },
  uk: { see: 'Показати переклад', original: 'Показати оригінал', translating: 'Переклад…', from: 'Перекладено з мови: {lang}', same: 'Вже вашою мовою' },
}

const strings = UI_STRINGS[DEVICE_LANG] || UI_STRINGS.en

/** Übersetzter UI-Text für die Gerätesprache */
export function t(key, vars = {}) {
  return strings[key].replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '')
}

const languageNames = typeof Intl.DisplayNames === 'function'
  ? (() => {
      try {
        return new Intl.DisplayNames([DEVICE_LOCALE, 'en'], { type: 'language' })
      } catch {
        return new Intl.DisplayNames(['en'], { type: 'language' })
      }
    })()
  : null

export function languageName(code) {
  try {
    return languageNames?.of(code.toLowerCase()) || code
  } catch {
    return code
  }
}

/** True, wenn die erkannte Ausgangssprache bereits der Gerätesprache entspricht */
export function isSameLanguage(sourceLang) {
  return sourceLang.split('-')[0] === TARGET_LANG.split('-')[0]
}

// Cache pro Sitzung, damit Hin- und Herschalten keine neuen Requests auslöst
const cache = new Map()

export function translate(text) {
  if (!cache.has(text)) {
    const request = fetch(`${API}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, target: TARGET_LANG }),
    }).then(async res => {
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Translation failed.')
      return data
    })
    // Fehlgeschlagene Requests nicht cachen, damit ein erneuter Klick es wieder versucht
    request.catch(() => cache.delete(text))
    cache.set(text, request)
  }
  return cache.get(text)
}
